import React, { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Download, Upload, FileSpreadsheet, ReceiptText, CalendarRange, Landmark, Sparkles, Wallet } from "lucide-react";
import { ImportStatusCards } from "@/components/ImportStatusCards";

const TABS = [
  {
    key: "expenses", label: "Έξοδα / Παραστατικά", icon: ReceiptText,
    desc: "Τιμολόγια προμηθευτών και έξοδα με κατηγορία USALI, ΦΠΑ και cost center.",
    template: ["/finance/template.xlsx", "TAF_Template_Parastatika.xlsx"],
    endpoint: "/finance/import",
  },
  {
    key: "bookings", label: "Κρατήσεις / Έσοδα", icon: CalendarRange,
    desc: "Κρατήσεις βιλών με πηγή (Direct NR / Direct Flex / OTA), ημερομηνίες και προμήθεια.",
    template: ["/finance/cashflow-template.xlsx?only=bookings", "TAF_Template_Kratiseis.xlsx"],
    endpoint: "/finance/import/cashflow",
  },
  {
    key: "obligations", label: "Υποχρεώσεις / Δόσεις", icon: Landmark,
    desc: "Δάνεια, ρυθμίσεις ΦΠΑ/φόρων, ασφαλιστικές δόσεις και επιταγές πληρωτέες.",
    template: ["/finance/cashflow-template.xlsx?only=obligations", "TAF_Template_Ypochreoseis.xlsx"],
    endpoint: "/finance/import/cashflow",
  },
  {
    key: "cheques", label: "Επιταγές", icon: Landmark,
    desc: "Επιταγές πληρωτέες: δικαιούχος, ποσό και ημερομηνία πληρωμής — μπαίνουν αυτόματα στο cash flow.",
    template: ["/finance/cashflow-template.xlsx?only=cheques", "TAF_Template_Epitages.xlsx"],
    endpoint: "/finance/import/cashflow",
  },
];

function ExpensePreview({ preview, suggestions }) {
  return (
    <div className="overflow-x-auto border border-[#E3DED4] rounded-xl" data-testid="imp-preview-expenses">
      <table className="w-full text-xs whitespace-nowrap">
        <thead>
          <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <th className="px-3 py-2">Γραμμή</th><th className="px-3 py-2">Τύπος</th><th className="px-3 py-2">Ημ/νία</th>
            <th className="px-3 py-2">Συναλλασσόμενος</th><th className="px-3 py-2">Κατηγορία</th><th className="px-3 py-2">CC</th>
            <th className="px-3 py-2 text-right">Καθαρή</th><th className="px-3 py-2 text-right">ΦΠΑ</th><th className="px-3 py-2">Κατάσταση</th>
            {suggestions && <th className="px-3 py-2">Πρόταση AI</th>}
          </tr>
        </thead>
        <tbody>
          {(preview.rows || []).map((r, idx) => (
            <tr key={r.row} data-testid={`imp-row-${r.row}`}
              className={`border-t border-[#E3DED4] ${r.errors.length ? "bg-[#FEF2F2]" : r.duplicate ? "bg-[#FFFBEB]" : ""}`}>
              <td className="px-3 py-1.5">{r.row}</td>
              <td className="px-3 py-1.5">{r.kind === "income" ? "Έσοδο" : "Έξοδο"}</td>
              <td className="px-3 py-1.5">{r.date || "—"}</td>
              <td className="px-3 py-1.5">{r.party}</td>
              <td className="px-3 py-1.5">{r.account_code} {r.account_name}</td>
              <td className="px-3 py-1.5">{r.cost_center}</td>
              <td className="px-3 py-1.5 text-right tabular">{fmtEuro(r.net)}</td>
              <td className="px-3 py-1.5 text-right tabular">{fmtEuro(r.vat)}</td>
              <td className="px-3 py-1.5">
                {r.errors.length ? <span className="text-[#991B1B]">{r.errors.join(", ")}</span>
                  : r.duplicate ? <span className="text-[#92400E]">Διπλή — θα παραλειφθεί</span>
                    : <span className="text-[#6E7A63]">OK</span>}
              </td>
              {suggestions && (
                <td className="px-3 py-1.5" data-testid={`imp-suggestion-${r.row}`}>
                  {suggestions[idx]
                    ? <span className="text-[#8A8577]">{suggestions[idx].account_code} · {suggestions[idx].cost_center}</span>
                    : <span className="text-[#6F6A60]">—</span>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BookingsPreview({ rows }) {
  return (
    <div className="border border-[#E3DED4] rounded-xl overflow-hidden text-xs" data-testid="imp-preview-bookings">
      <div className="px-3 py-2 bg-[#F1EDE6] font-semibold">Κρατήσεις ({rows.length})</div>
      {rows.map((b) => (
        <div key={b.row} data-testid={`imp-bk-${b.row}`}
          className={`px-3 py-2 border-t border-[#E3DED4] flex justify-between gap-3 ${b.errors.length ? "bg-[#FEF2F2]" : ""}`}>
          <span>{b.guest || "—"} · {b.unit || "—"} · {b.checkin} → {b.checkout}</span>
          <span className="tabular">{fmtEuro(b.gross)}{b.errors.length ? <em className="text-[#991B1B] ml-2">{b.errors.join(", ")}</em> : null}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="px-3 py-4 text-[#6F6A60]">Κανένα φύλλο «Κρατήσεις» στο αρχείο.</div>}
    </div>
  );
}

function ObligationsPreview({ rows }) {
  return (
    <div className="border border-[#E3DED4] rounded-xl overflow-hidden text-xs" data-testid="imp-preview-obligations">
      <div className="px-3 py-2 bg-[#F1EDE6] font-semibold">Υποχρεώσεις ({rows.length})</div>
      {rows.map((o) => (
        <div key={o.row} data-testid={`imp-ob-${o.row}`}
          className={`px-3 py-2 border-t border-[#E3DED4] flex justify-between gap-3 ${o.errors.length ? "bg-[#FEF2F2]" : ""}`}>
          <span>{o.type} · {o.creditor || o.description}</span>
          <span className="tabular">{fmtEuro(o.instalment)} × {o.months}{o.errors.length ? <em className="text-[#991B1B] ml-2">{o.errors.join(", ")}</em> : null}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="px-3 py-4 text-[#6F6A60]">Κανένα φύλλο «Υποχρεώσεις» στο αρχείο.</div>}
    </div>
  );
}

function TaxEfkaImport() {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const send = useMutation({
    mutationFn: async (commit) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/payroll/import/tax-efka?year=2026&commit=${commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      setRes(d);
      if (d.commit) { qc.invalidateQueries({ queryKey: ["runs"] }); toast.success(`Ενημερώθηκαν ${d.runs} μισθοδοσίες · Φόρος ${fmtEuro(d.tax_total)} · ΕΦΚΑ ${fmtEuro(d.efka_total)}`); }
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="border-t border-[#E3DED4] pt-4 space-y-2" data-testid="imp-tax-efka">
      <p className="text-xs text-[#6F6A60]">ΦΜΥ & ΕΦΚΑ: διαβάζονται αυτόματα από το αρχείο μισθοδοσίας αν έχει στήλες «Φόρος» και «ΕΦΚΑ (σύνολο)». Εναλλακτικά, ανεβάστε το αρχείο του λογιστή «Φόρος & ΕΦΚΑ ανά εργαζόμενο» (φύλλο «Ανά μήνα») — ενημερώνει μόνο τα πεδία ΦΜΥ και ΕΦΚΑ σε κάθε μισθοδοσία.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="imp-tax-efka-file" type="file" accept=".xlsx" className="text-sm" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); }} />
        <button data-testid="imp-tax-efka-preview" disabled={!file || send.isPending} onClick={() => send.mutate(false)}
          className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40"><Upload className="h-4 w-4" /> Προεπισκόπηση</button>
        {res && !res.commit && (
          <button data-testid="imp-tax-efka-commit" disabled={send.isPending} onClick={() => send.mutate(true)}
            className="rounded-xl bg-[#6E7A63] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Ενημέρωση {res.updated.length} γραμμών</button>
        )}
      </div>
      {res && (
        <div className="text-xs text-[#45423C] space-y-1" data-testid="imp-tax-efka-result">
          <p>{res.updated.length} γραμμές σε {res.runs} μισθοδοσίες · Φόρος {fmtEuro(res.tax_total)} · ΕΦΚΑ {fmtEuro(res.efka_total)} {res.commit ? "— καταχωρήθηκε" : ""}</p>
          {res.unmatched.map((u) => <p key={u.period + u.surname} className="text-[#92400E]">Δεν μπήκε: {u.period} · {u.surname} ({u.reason})</p>)}
        </div>
      )}
    </div>
  );
}

function PayrollHistoryImport({ canEdit }) {
  const filesRef = useRef();
  const totalRef = useRef();
  const qc = useQueryClient();
  const [picked, setPicked] = useState([]);
  const [preview, setPreview] = useState(null);

  const send = useMutation({
    mutationFn: async (commit) => {
      const fd = new FormData();
      picked.forEach((f) => fd.append("files", f));
      return (await api.post(`/payroll/import/${commit ? "commit?replace=true" : "preview"}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d, commit) => {
      if (commit) {
        setPreview(null);
        setPicked([]);
        if (filesRef.current) filesRef.current.value = "";
        qc.invalidateQueries({ queryKey: ["runs"] });
        toast.success(`Καταχωρήθηκαν ${d.runs} μισθοδοσίες (${d.years.join(", ")}) · ${fmtEuro(d.total_gross)}`);
      } else {
        setPreview(d);
        toast.success(`Αναγνωρίστηκαν ${d.files.length} αρχεία · ${fmtEuro(d.total_gross)}`);
      }
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const yearly = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      return (await api.post("/payroll/import/yearly", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["budget-lines"] });
      toast.success(`Προϋπολογισμός ${d.year}: ${d.lines} υπάλληλοι · ${fmtEuro(d.total)}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="imp-payroll-history">
      <div>
        <h2 className="font-medium text-[#45423C] flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[#C86235]" /> Ιστορικό μισθοδοσίας (παλαιότερα έτη)
        </h2>
        <p className="text-sm text-[#6F6A60] mt-1">
          Ανεβάστε τα αρχεία μισθοδοσίας όπως τα έχετε (π.χ. «TAF 3ος 2025.xlsx», «Πασχα 2024.xlsx»). Το έτος και ο
          τύπος (μηνιαία, Πάσχα, Επίδομα/Αποζημείωση Αδείας, Χριστούγεννα) αναγνωρίζονται από το όνομα του αρχείου.
          Μπαίνουν ως οριστικοποιημένες μισθοδοσίες για σύγκριση με τα επόμενα έτη.
        </p>
      </div>

      {!canEdit ? (
        <p className="text-sm text-[#991B1B]">Μόνο Ιδιοκτήτης / HR μπορούν να ανεβάσουν αρχεία.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 border-t border-[#E3DED4] pt-4">
            <input data-testid="imp-payroll-files" ref={filesRef} type="file" accept=".xlsx,.xlsm" multiple className="text-sm"
              onChange={(e) => { setPicked(Array.from(e.target.files || [])); setPreview(null); }} />
            <button data-testid="imp-payroll-preview" onClick={() => send.mutate(false)} disabled={!picked.length || send.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-40">
              <Upload className="h-4 w-4" /> Προεπισκόπηση ({picked.length})
            </button>
            {preview && (
              <button data-testid="imp-payroll-commit" onClick={() => send.mutate(true)} disabled={send.isPending}
                className="rounded-xl bg-[#6E7A63] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                {send.isPending ? "Καταχώρηση…" : `Καταχώρηση ${preview.files.length} μισθοδοσιών`}
              </button>
            )}
          </div>

          {preview && (
            <div className="overflow-x-auto border border-[#E3DED4] rounded-xl" data-testid="imp-payroll-preview-table">
              <table className="w-full text-xs whitespace-nowrap">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                    <th className="px-3 py-2">Αρχείο</th><th className="px-3 py-2">Έτος</th>
                    <th className="px-3 py-2">Μισθοδοσία</th><th className="px-3 py-2 text-right">Υπάλληλοι</th>
                    <th className="px-3 py-2 text-right">Σύνολο</th><th className="px-3 py-2">Κατάσταση</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.files.map((f) => (
                    <tr key={f.filename} data-testid={`imp-payroll-row-${f.year}-${f.run_type}-${f.month}`} className="border-t border-[#E3DED4]">
                      <td className="px-3 py-1.5 max-w-[240px] truncate" title={f.filename}>{f.filename}</td>
                      <td className="px-3 py-1.5 tabular">{f.year}</td>
                      <td className="px-3 py-1.5">{f.label_el}</td>
                      <td className="px-3 py-1.5 text-right tabular">{f.employees}</td>
                      <td className="px-3 py-1.5 text-right tabular font-semibold">{fmtEuro(f.total_gross)}</td>
                      <td className="px-3 py-1.5">
                        {f.exists ? <span className="text-[#92400E]">θα αντικατασταθεί</span> : <span className="text-[#6E7A63]">νέα</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#45423C] text-white">
                    <td className="px-3 py-2 font-semibold" colSpan={4}>Σύνολο</td>
                    <td className="px-3 py-2 text-right tabular font-semibold" colSpan={2}>{fmtEuro(preview.total_gross)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <TaxEfkaImport />

          <div className="border-t border-[#E3DED4] pt-4">
            <p className="text-xs text-[#6F6A60] mb-2">
              Προαιρετικά: αρχείο «TAF Total &lt;έτος&gt;» για τον προϋπολογισμό/σύνολα του έτους (σύγκριση πραγματικών με στόχο).
            </p>
            <input data-testid="imp-payroll-total-file" ref={totalRef} type="file" accept=".xlsx,.xlsm" className="text-sm"
              onChange={(e) => e.target.files?.[0] && yearly.mutate(e.target.files[0])} />
            {yearly.isPending && <span className="text-xs text-[#6F6A60] ml-2">Ανάγνωση…</span>}
          </div>
        </>
      )}
    </div>
  );
}

export default function Imports({ only = null, embedded = false }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const tabs = only ? TABS.filter((t) => only.includes(t.key)) : TABS;
  const [tab, setTab] = useState(tabs[0]?.key || "expenses");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const fileRef = useRef(null);
  const active = TABS.find((t) => t.key === tab) || TABS[0];

  const suggest = useMutation({
    mutationFn: async () => (await api.post("/ai/categorize", {
      rows: (preview?.rows || []).map((r) => ({ party: r.party, description: r.description || "", amount: r.net })),
    })).data,
    onSuccess: (d) => {
      const map = {};
      (d.suggestions || []).forEach((s) => { map[s.i] = s; });
      setSuggestions(map);
      toast.success(`Το AI πρότεινε κατηγορίες για ${Object.keys(map).length} γραμμές`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const reset = () => { setFile(null); setPreview(null); setSuggestions(null); if (fileRef.current) fileRef.current.value = ""; };
  const switchTab = (k) => { setTab(k); reset(); };

  const upload = useMutation({
    mutationFn: async (commit) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`${active.endpoint}/${commit ? "commit" : "preview"}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d, commit) => {
      if (!commit) {
        setPreview(d);
        toast.info(tab === "expenses"
          ? `${d.valid} έγκυρες · ${d.invalid} με λάθη · ${d.duplicates} διπλές`
          : `${d.valid_bookings} κρατήσεις · ${d.valid_obligations} υποχρεώσεις · ${d.invalid} με λάθη`);
        return;
      }
      reset();
      ["entries", "pl-report", "pl-kpi", "bookings", "obligations", "cashflow"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] }));
      toast.success(tab === "expenses"
        ? `Εισήχθησαν ${d.imported} παραστατικά (παραλείφθηκαν ${d.skipped})`
        : `Εισήχθησαν ${d.bookings_imported} κρατήσεις και ${d.obligations_imported} υποχρεώσεις`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const commitLabel = () => {
    if (!preview) return "";
    if (tab === "expenses") return `Εισαγωγή ${preview.valid} γραμμών (${fmtEuro(preview.total_net)})`;
    if (tab === "bookings") return `Εισαγωγή ${preview.valid_bookings} κρατήσεων`;
    if (tab === "cheques") return `Εισαγωγή ${preview.valid_obligations} επιταγών`;
    return `Εισαγωγή ${preview.valid_obligations} υποχρεώσεων`;
  };
  const commitDisabled = upload.isPending || !preview ||
    (tab === "expenses" ? !preview.valid : tab === "bookings" ? !preview.valid_bookings : !preview.valid_obligations);

  return (
    <div className="space-y-6" data-testid={embedded ? "imports-embedded" : "imports-page"}>
      {!embedded && <ImportStatusCards />}
      {!embedded && <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Εισαγωγές Excel</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Κέντρο Εισαγωγών</h1>
        <p className="text-[#6F6A60] text-sm mt-1">Κάθε τύπος δεδομένων έχει ξεχωριστό πρότυπο και ξεχωριστό ανέβασμα — τα έξοδα δεν μπερδεύονται με τις κρατήσεις.</p>
      </div>}

      {tabs.length > 0 && <>
      {tabs.length > 1 && <div className="flex flex-wrap gap-2" data-testid="imp-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          const on = t.key === tab;
          return (
            <button key={t.key} data-testid={`imp-tab-${t.key}`} onClick={() => switchTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                on ? "bg-[#45423C] text-white" : "bg-white border border-[#E3DED4] text-[#6F6A60] hover:border-[#45423C]"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>}

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="imp-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-medium text-[#45423C] flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-[#C86235]" /> {active.label}
            </h2>
            <p className="text-sm text-[#6F6A60] mt-1">{active.desc}</p>
          </div>
          <button data-testid="imp-template" onClick={() => downloadXlsx(active.template[0], active.template[1])}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
            <Download className="h-4 w-4" /> Πρότυπο Excel
          </button>
        </div>

        {!canEdit ? (
          <p className="text-sm text-[#991B1B]">Μόνο Ιδιοκτήτης / HR μπορούν να ανεβάσουν αρχεία.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 border-t border-[#E3DED4] pt-4">
              <input data-testid="imp-file" ref={fileRef} type="file" accept=".xlsx" className="text-sm"
                onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
              <button data-testid="imp-preview-btn" onClick={() => upload.mutate(false)} disabled={!file || upload.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-40">
                <Upload className="h-4 w-4" /> Προεπισκόπηση
              </button>
              {preview && (
                <button data-testid="imp-commit-btn" onClick={() => upload.mutate(true)} disabled={commitDisabled}
                  className="rounded-xl bg-[#6E7A63] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                  {commitLabel()}
                </button>
              )}
              {preview && tab === "expenses" && (
                <button data-testid="imp-ai-suggest" onClick={() => suggest.mutate()} disabled={suggest.isPending}
                  className="inline-flex items-center gap-2 rounded-xl border border-[#B9AE9A] bg-[#F7F2E8] px-4 py-2 text-sm font-semibold text-[#8A8577] transition-colors hover:bg-[#B9AE9A]/20 disabled:opacity-50">
                  <Sparkles className="h-4 w-4" /> {suggest.isPending ? "Ανάλυση AI…" : "Πρότασε κατηγορίες (AI)"}
                </button>
              )}
            </div>
            {preview && tab === "expenses" && <ExpensePreview preview={preview} suggestions={suggestions} />}
            {preview && tab === "bookings" && <BookingsPreview rows={preview.bookings || []} />}
            {preview && (tab === "obligations" || tab === "cheques") && <ObligationsPreview rows={preview.obligations || []} />}
            {preview && tab !== "expenses" && (
              <p className="text-xs text-[#6F6A60]">
                Σημείωση: η εισαγωγή καταχωρεί όλες τις έγκυρες γραμμές του αρχείου (κρατήσεις και/ή υποχρεώσεις).
              </p>
            )}
          </>
        )}
      </div>
      </>}

      {(!only || only.includes("payroll")) && <PayrollHistoryImport canEdit={canEdit} />}
    </div>
  );
}
